import { cn } from "@/lib/utils";
import { useTheme } from "@/components/ThemeProvider";

/**
 * The Zen mark with its morphing glow — the app's single loading indicator.
 *
 * This markup was previously copy-pasted in four places (three in routes.tsx, one
 * in RoleGuard.tsx), each carrying its own inline `animation:` shorthands naming
 * `morphingGlow` / `glowPulse1..3`. Those keyframes are declared only inside
 * SearchPage.tsx's inline <style>, so everywhere else in the app the names
 * resolved to nothing and the "animated" loader rendered as a static image. The
 * .zen-loader-* classes used here carry the same geometry against real,
 * globally-declared keyframes in index.css.
 *
 * Sizing lives on the caller: pass `className` for anything other than the
 * default 300px square.
 */
export function ZenLoader({ className }: { className?: string }) {
  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? "/zen-logo.png" : "/zen-black-logo.png";

  return (
    <div
      className={cn("loader-container relative h-[300px] w-[300px]", className)}
      role="status"
      aria-label="Loading"
    >
      {/* Decorative: the wrapper already announces the loading state. */}
      <img
        src={logoSrc}
        alt=""
        aria-hidden="true"
        className="loader-image zen-loader-mark relative z-10"
      />
      <div className="zen-loader-glow-1 absolute inset-0 rounded-full" aria-hidden="true" />
      <div className="zen-loader-glow-2 absolute inset-0 rounded-full" aria-hidden="true" />
      <div className="zen-loader-glow-3 absolute inset-0 rounded-full" aria-hidden="true" />
    </div>
  );
}

/**
 * The loader as a full-viewport screen. Used before the app shell exists (auth
 * boot, the lazy-route Suspense fallback) — anywhere there is no Layout yet to
 * fill.
 */
export function ZenLoaderScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <ZenLoader />
    </div>
  );
}
